import React, { useContext } from "react";
import {
  Drawer,
  IconButton,
  Typography,
  Divider,
} from "@mui/material";
import { CheckCircle, Close as CloseIcon, RadioButtonUnchecked } from "@mui/icons-material";
import "../../assets/styles/TrackOrder.scss";
import { GaintOFDOrdersContext } from "../../context";

const TrackOrder = () => {
  const { openTracking, setOpenTracking, trackingInfo } = useContext(GaintOFDOrdersContext);

  // Sort by latest first
  const sortedData = (trackingInfo && trackingInfo.length > 0) ? [...trackingInfo].sort(
    (a, b) => new Date(b.created_at) - new Date(a.created_at)
  ) : [];

  // Group events by date (yyyy-mm-dd)
  const groupedByDate = sortedData && sortedData.length > 0 && sortedData.reduce((acc, item) => {
    const d = new Date(item.created_at);
    const key = d.toISOString().split("T")[0];
    if (!acc[key]) acc[key] = [];
    acc[key].push(item);
    return acc;
  }, {});

  const formatDate = (dateStr) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-GB", {
      weekday: "short",
      day: "2-digit",
      month: "short",
    });
  };

  const getDaysDifference = (events = []) => {
    if (!events || events.length === 0) return null;

    // Find earliest created_at
    const minDate = new Date(
      Math.min(...events.map((e) => new Date(e.created_at).getTime()))
    );

    // If delivered, use delivered date as end date; else use today
    const deliveredEvent = events.find((e) => e.status_code === "8"); // delivered
    const maxDate = deliveredEvent
      ? new Date(deliveredEvent.created_at)
      : new Date();

    const diffTime = maxDate - minDate;
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24)); // days
  };

  const daysTaken = getDaysDifference(trackingInfo);

  return (
    <>
      {/* Drawer */}
      <Drawer
        anchor="right"
        open={openTracking}
        onClose={() => setOpenTracking(false)}
        className="order-track-drawer"
        classes={{ paper: "track-order-drawer" }}
      >
        {/* Header */}
        <div className="drawer-header">
          <Typography variant="h6" className="drawer-title">
            Track Order
          </Typography>
          <IconButton onClick={() => setOpenTracking(false)} size="small" className="close-btn">
            <CloseIcon />
          </IconButton>
        </div>

        <div className="drawer-content">
          {/* Top order info */}
          <div className="order-info-card">
            <div className="info-row">
              <Typography variant="body2" className="label">AWB Number</Typography>
              <Typography variant="body1" className="value">
                {trackingInfo && trackingInfo.length > 0 && trackingInfo[0]?.awb_no}
              </Typography>
            </div>
            <div className="info-row">
              <Typography variant="body2" className="label">Time Elapsed</Typography>
              <Typography variant="body1" className="value">
                {daysTaken !== null ? `${daysTaken} Day${daysTaken !== 1 ? 's' : ''}` : "NA"}
              </Typography>
            </div>
          </div>

          <Divider className="divider" />

          {/* Timeline */}
          <div className="timeline">
            {Object.keys(groupedByDate).map((dateKey, idx) => (
              <div key={dateKey} className="timeline-group">
                {/* Date Group */}
                <div className="date-header-wrapper">
                  <Typography variant="subtitle2" className="date-header">
                    {formatDate(dateKey)}
                  </Typography>
                </div>

                {groupedByDate[dateKey].map((item, index) => {
                  const dateObj = new Date(item.created_at);
                  const time = dateObj.toLocaleTimeString([], {
                    hour: "2-digit",
                    minute: "2-digit",
                  });

                  // Check if this is the very first item (latest event)
                  const isLatest = idx === 0 && index === 0;

                  return (
                    <div className={`timeline-item ${isLatest ? 'latest' : ''}`} key={item.id}>
                      <div className="timeline-left">
                        <Typography variant="caption" className="time">
                          {time}
                        </Typography>
                      </div>

                      <div className="timeline-icon-container">
                        {isLatest ? (
                          <CheckCircle className="icon-active" />
                        ) : (
                          <RadioButtonUnchecked className="icon-inactive" />
                        )}
                        {(index !== groupedByDate[dateKey].length - 1 ||
                          idx !== Object.keys(groupedByDate).length - 1) && (
                            <div className="line" />
                          )}
                      </div>

                      <div className="timeline-content">
                        <Typography variant="body1" className="status">
                          {item.status_msg}
                        </Typography>

                        {item.location && (
                          <Typography variant="body2" className="location">
                            📍 {item.location}
                          </Typography>
                        )}

                        {item.assign_user_id && (
                          <Typography variant="caption" className="remarks">
                            👤 {item.assign_user_id}
                          </Typography>
                        )}

                        {item.scan_remarks && item.scan_remarks !== "undefined" && (
                          <Typography variant="caption" className="remarks">
                            📝 {item.scan_remarks}
                          </Typography>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      </Drawer>
    </>
  );
};

export default TrackOrder;
